import React from 'react';
import styled from 'styled-components';
import { AimOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { PairOpenMode } from '../utils/pairSelection';

const OPTIONS: Array<{ value: PairOpenMode; label: string; hint: string; icon: React.ReactNode }> = [
    { value: 'IMMEDIATE', label: 'Auto', hint: 'Opens right after Start', icon: <ThunderboltOutlined /> },
    { value: 'REFERENCE', label: 'Waiting', hint: 'Waits for your target prices', icon: <AimOutlined /> },
];

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`;

const Option = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid ${({ $active }) => ($active ? '#0ECB81' : '#2B3139')};
  background: ${({ $active }) => ($active ? 'rgba(14, 203, 129, 0.08)' : '#1E2329')};
  color: ${({ $active }) => ($active ? '#EAECEF' : '#848E9C')};
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    border-color: ${({ $active }) => ($active ? '#0ECB81' : '#5E6673')};
    color: #EAECEF;
  }
  &:focus-visible {
    outline: 2px solid rgba(14, 203, 129, 0.5);
    outline-offset: 1px;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: ${({ $active }) => ($active ? 0.75 : 0.4)};
  }
`;

const Icon = styled.span<{ $active: boolean }>`
  display: inline-flex;
  font-size: 15px;
  color: ${({ $active }) => ($active ? '#0ECB81' : 'inherit')};
`;

const Text = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
`;

const Title = styled.span`
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Hint = styled.span`
  font-size: 10.5px;
  color: #848E9C;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface Props {
    value: PairOpenMode;
    onChange: (next: PairOpenMode) => void;
    disabled?: boolean;
}

const EntryModeToggle: React.FC<Props> = ({ value, onChange, disabled }) => (
    <Row role="radiogroup" aria-label="Mode">
        {OPTIONS.map((o) => {
            const active = o.value === value;
            return (
                <Option
                    key={o.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    $active={active}
                    disabled={disabled}
                    onClick={() => !active && onChange(o.value)}
                >
                    <Icon $active={active}>{o.icon}</Icon>
                    <Text>
                        <Title>{o.label}</Title>
                        <Hint>{o.hint}</Hint>
                    </Text>
                </Option>
            );
        })}
    </Row>
);

export default EntryModeToggle;
